import React, { useMemo } from 'react';
import { ModalName } from 'structure/staking/types';
import { useTranslation } from 'react-i18next';
import { useChainBlock } from 'utils/hooks/useChainBlock';
import { StakingPool, StakingProject } from 'api/StakingAPI';
import {
  useStakingPoolData,
  useStakingUserPoolInfo,
} from 'structure/staking/hooks/useStakingAPI';
import { StakingPoolIcon } from 'structure/staking/components/StakingPoolIcon';
import { StakingPoolActions } from 'structure/staking/components/StakingPoolActions';
import { StakingCountdown } from 'structure/staking/components/StakingCountdown';
import Paragraph from 'structure/launchpad/components/Typography';
import { UserStaked } from 'structure/staking/components/UserStaked';
import { UserClaimable } from 'structure/staking/components/UserClaimable';
import { useConnection } from 'wagmi';
import { unixTimestampToMs } from 'utils/formateDate';
import { Spinner } from 'baseui/spinner';

type StakingPoolCardProps = {
  pool: StakingPool;
  project?: StakingProject;
  onOpenModal: (modalName: ModalName, poolId: number) => void;
};

export function StakingPoolCard({
  pool: initialPool,
  onOpenModal,
}: StakingPoolCardProps) {
  const { t } = useTranslation('common');
  const account = useConnection();
  const { data: chainBlock } = useChainBlock();
  const stakingPoolData = useStakingPoolData(initialPool);
  const pool = stakingPoolData.data;
  const stakingUserPoolInfo = useStakingUserPoolInfo({
    poolContractAddress: pool?.stakingContractAddress,
    walletAddress: account.address as `0x${string}`,
    chainId: pool?.chainId,
    pool,
  });

  const handleOpenModal = (modalName: ModalName, poolId: number) => {
    onOpenModal(modalName, poolId);
    stakingUserPoolInfo.refetch();
  };

  const isStakingPoolDataLoading = useMemo(
    () => stakingPoolData.isLoading || !stakingPoolData.isFetched,
    [stakingPoolData]
  );
  const isStakingUserPoolInfoLoading = useMemo(
    () =>
      isStakingPoolDataLoading ||
      stakingUserPoolInfo.isLoading ||
      !stakingUserPoolInfo.isFetched,
    [stakingUserPoolInfo, isStakingPoolDataLoading]
  );
  return (
    <div className='rounded-main flex flex-col gap-6 bg-[#141314] px-4 py-6'>
      <div className='grid grid-cols-2 gap-2'>
        <StakingPoolIcon pool={initialPool} />
        {isStakingUserPoolInfoLoading ? (
          <Spinner $size='14px' $borderWidth='3px' className='mx-auto' />
        ) : (
          <StakingCountdown
            id={pool.stakingContractAddress}
            inline
            date={unixTimestampToMs(
              parseInt(stakingUserPoolInfo.data?.unlockDate || '0')
            )}
            timestamp={chainBlock.timestamp}
          />
        )}
        <div className='col-span-2 h-6' />

        <div className='flex flex-row items-center gap-2'>
          <Paragraph className='text-sm font-semibold text-[#F7F8F8]'>
            {t('STAKING.TVL')}
          </Paragraph>
          <Paragraph className='text-sm text-[#B4BCD0]'>
            {isStakingPoolDataLoading ? (
              <Spinner $size='14px' $borderWidth='3px' className='mx-auto' />
            ) : (
              `${t('currency', { value: pool.totalValueLockedUSD })}`
            )}
          </Paragraph>
        </div>
        <div className='flex flex-row items-center justify-end gap-2'>
          <Paragraph className='text-sm font-semibold text-[#F7F8F8]'>
            {t('STAKING.STAKED')}
          </Paragraph>
          {isStakingUserPoolInfoLoading ? (
            <Spinner $size='14px' $borderWidth='3px' className='mx-auto' />
          ) : (
            <UserStaked pool={pool} userPoolInfo={stakingUserPoolInfo.data} />
          )}
        </div>
        <div className='flex flex-row items-center gap-2'>
          <Paragraph className='text-xs font-semibold text-[#F7F8F8]'>
            {t('STAKING.APR')}
          </Paragraph>
          <Paragraph className='text-xs text-[#B4BCD0]'>
            {isStakingUserPoolInfoLoading ? (
              <Spinner $size='14px' $borderWidth='3px' className='mx-auto' />
            ) : (
              `${parseFloat(stakingUserPoolInfo.data?.apr || '0').toFixed(2)}%`
            )}
          </Paragraph>
        </div>
        <div className='flex flex-row items-center justify-end gap-2'>
          <Paragraph className='text-sm font-semibold text-[#F7F8F8]'>
            {t('STAKING.CLAIMABLE')}
          </Paragraph>
          {isStakingUserPoolInfoLoading ? (
            <Spinner $size='14px' $borderWidth='3px' className='mx-auto' />
          ) : (
            <UserClaimable
              pool={pool}
              userPoolInfo={stakingUserPoolInfo.data}
            />
          )}
        </div>
        <div className='col-span-2 h-6' />
        <div className='col-span-2'>
          <StakingPoolActions
            pool={pool}
            userPoolInfo={stakingUserPoolInfo.data}
            onOpenModal={handleOpenModal}
          />
        </div>
      </div>
    </div>
  );
}
